import {
  LabReportControllerService,
  LabReportUploadRequest,
  UpdateTaskRequest
} from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import { KnownProgram } from "src/constants/programs";
import { tracker } from "src/state/state";
import TaskManagementBloc, {
  TaskKey
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import { AppQueryPopupsController } from "../AppQueryPopups/AppQueryPopupsBloc";
import BlockingLoadingOverlayController from "../BlockingLoadingOverlay/BlockingLoadingOverlayController";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";

export type Step =
  | "upload"
  | "unsupportedFormat"
  | "providerSelection"
  | "howToGetReport"
  | "pdfInfo"
  | "almostThere"
  | "goodNews"
  | "willHelpYou";

export type SelectedFileMeta = {
  type: string;
  name: string;
};

export type ProviderChoice = "labcorp" | "quest" | "none";

interface ChatUploadLabResultsState {
  currentStep: Step;
  selectedFilesMeta: Record<string, SelectedFileMeta>;
  providerChoice: ProviderChoice | null;
}

export class ChatUploadLabResultsCubit extends Cubit<ChatUploadLabResultsState> {
  constructor() {
    super({
      currentStep: "upload",
      selectedFilesMeta: {},
      providerChoice: null
    });
  }

  handleSkipLabReportUpload = () => {
    const taskManagementState = Blac.getBloc(TaskManagementBloc);
    tracker.track("Lab-Report-Upload Skipped");
    void taskManagementState
      .updateTaskStatus(
        {
          program: KnownProgram.ONBOARDING,
          group: "labs",
          slug: TaskKey.UPLOAD_LABS
        },
        UpdateTaskRequest.status.SKIPPED
      )
      .then(() => {
        AppQueryPopupsController.closePopup();
      });
  };

  setCurrentStep = (step: Step) => {
    this.patch({ currentStep: step });
  };

  setSelectedFilesMeta = (docName: string, file: File) => {
    this.patch({
      selectedFilesMeta: {
        ...this.state.selectedFilesMeta,
        [docName]: { type: file.type ?? "", name: file.name }
      }
    });
  };

  syncSelectedFilesMeta = (files: Record<string, string>) => {
    const updatedEntries = Object.entries(this.state.selectedFilesMeta).filter(
      ([key]) => files[key]
    );
    this.patch({
      selectedFilesMeta: Object.fromEntries(updatedEntries)
    });
  };

  setProviderChoice = (providerChoice: ProviderChoice | null) => {
    this.patch({ providerChoice });
  };

  isPdfMeta = (meta: SelectedFileMeta) => {
    const normalizedType = meta.type.toLowerCase();
    if (
      normalizedType === "application/pdf" ||
      normalizedType === "application/x-pdf"
    ) {
      return true;
    }
    return meta.name.toLowerCase().endsWith(".pdf");
  };

  handleSubmitLabReport = async (
    filesIdList: string[],
    provider?: LabReportUploadRequest["provider"]
  ) => {
    if (filesIdList.length === 0) {
      return;
    }

    const taskManagementState = Blac.getBloc(TaskManagementBloc);
    const [loadingDone] = BlockingLoadingOverlayController.startLoading({
      bg: "transparent"
    });

    try {
      await LabReportControllerService.uploadLabReport({
        fileIds: filesIdList,
        provider
      });

      const uploadTask = taskManagementState.getTask(TaskKey.UPLOAD_LABS);
      if (uploadTask) {
        await taskManagementState.updateTaskStatus(
          uploadTask,
          UpdateTaskRequest.status.COMPLETED
        );
      }
    } catch (error) {
      reportErrorSentry(error);
      BlockingLoadingOverlayController.loadingError({
        title: translate("error_unknown_error")
      });
      throw error;
    } finally {
      loadingDone();
    }
  };
}
